import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';
import { Typography, Box, Grid2 as Grid, useMediaQuery } from '@mui/material';

interface ChartItem {
  name: string;
  value: number;
  color: string;
  extraLabel?: string;
}

interface BalancePieChartProps {
  title: string;
  chartData: ChartItem[];
  totalAvailable: number;
  nextPeriodText?: React.ReactNode;
}

const BalancePieChart: React.FC<BalancePieChartProps> = ({ title, chartData, totalAvailable, nextPeriodText }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return (
    <Box>
      <Typography variant="h5" mb={2}>
        {title}
      </Typography>

      <Grid container spacing={3} alignItems="center">
        <Grid size={{ xs: 6, sm: 12, md: 5 }}>
          <Box
            position="relative"
            sx={{
              height: { xs: 130, sm: 170 },
              width: { xs: 130, sm: 170 },
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 40 : 60}
                  outerRadius={isMobile ? 60 : 80}
                  dataKey="value"
                >
                  {chartData.map(entry => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  allowEscapeViewBox={{
                    x: true,
                    y: true,
                  }}
                  content={(props: TooltipProps<number, string>) => {
                    if (props.active && props.payload?.length) {
                      const { name, value } = props.payload[0] as { name: string; value: number };

                      return (
                        <Box
                          bgcolor="var(--mui-palette-background-paper)"
                          p={1}
                          border={1}
                          borderColor="border.default"
                          borderRadius={1}
                          sx={{
                            opacity: 0.8,
                          }}
                        >
                          <Typography variant="body1" noWrap>
                            {name}: {value}d
                          </Typography>
                        </Box>
                      );
                    }

                    return null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box
              position="absolute"
              top="50%"
              left="50%"
              sx={{ transform: 'translate(-50%, -50%)', textAlign: 'center' }}
            >
              <Typography variant="h4">{totalAvailable}d</Typography>
              <Typography variant="body1" color="textSecondary">
                available
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 6, sm: 12, md: 7 }}
          sx={{
            mt: { xs: 2, md: 0 },
            minHeight: { xs: 90, md: 0 },
          }}
        >
          <Box display="flex" flexDirection="column" gap={1.5}>
            {chartData.map(item => (
              <Box key={item.name} display="flex" alignItems="center" gap={1}>
                <Box minWidth={16} height={16} bgcolor={item.color} borderRadius="50%" />
                <Typography variant="body1">
                  {item.extraLabel
                    ? `${item.name} — ${item.extraLabel}`
                    : `${item.name} — ${item.value} ${item.value === 1 ? 'day' : 'days'}`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      {nextPeriodText && (
        <Box mt={2}>
          <Typography variant="body1">{nextPeriodText}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default BalancePieChart;
